import { useEffect, useRef, useState } from 'react';
import { loadPlaces, Place, searchAddress, suggestPlaces } from './geo';

type Props = {
    placeholder: string,
    onPick: (place: Place) => void,
    autoFocus?: boolean,
};

// A text box that turns a ZIP code, a city or a street address into a place.
export default function PlaceSearch({ placeholder, onPick, autoFocus }: Props) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [addresses, setAddresses] = useState<Place[] | undefined>();
    const [searching, setSearching] = useState(false);
    const [message, setMessage] = useState('');
    const [active, setActive] = useState(0);
    const [, setReady] = useState(false);
    const root = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadPlaces().then(() => setReady(true));
    }, []);

    useEffect(() => {
        const close = (event: MouseEvent) => {
            if (!root.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    const text = query.trim();
    const suggestions = addresses ?? suggestPlaces(text);
    const canSearchAddress = !addresses && text.length >= 4;
    const optionCount = suggestions.length + (canSearchAddress ? 1 : 0);

    const pick = (place: Place) => {
        onPick(place);
        setQuery('');
        setAddresses(undefined);
        setOpen(false);
    };

    const lookUpAddress = async () => {
        setSearching(true);
        setMessage('');
        try {
            const results = await searchAddress(text);
            setAddresses(results);
            setActive(0);
            if (!results.length) {
                setMessage('Nothing found for that address');
            }
        } catch (error) {
            setMessage((error as Error).message);
        }
        setSearching(false);
    };

    const choose = (index: number) => {
        if (index < suggestions.length) {
            pick(suggestions[index]);
        } else if (canSearchAddress) {
            lookUpAddress();
        }
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((current) => (current + (event.key === 'ArrowDown' ? 1 : -1) + optionCount) % Math.max(optionCount, 1));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            choose(active);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div className='place-search' ref={root}>
            <input
                value={query}
                placeholder={placeholder}
                autoFocus={autoFocus}
                onChange={(event) => { setQuery(event.target.value); setAddresses(undefined); setMessage(''); setActive(0); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
            />
            {open && text.length >= 2 && (optionCount > 0 || message) && <ul className='place-options'>
                {suggestions.map((place, index) => <li
                    key={place.label}
                    className={index === active ? 'active' : ''}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => pick(place)}
                >{place.label}</li>)}
                {canSearchAddress && <li
                    className={`address-option ${active === suggestions.length ? 'active' : ''}`}
                    onMouseEnter={() => setActive(suggestions.length)}
                    onClick={lookUpAddress}
                >{searching ? 'Searching…' : <>Search for the address “{text}”</>}</li>}
                {message && <li className='place-message'>{message}</li>}
            </ul>}
        </div>
    );
}
